import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, X, ArrowRight, Video, MapPin, Globe2 } from "lucide-react";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";
import CoachBadges from "@/components/coachly/CoachBadges";
import { StarsDisplay } from "@/components/coachly/StarRating";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------------------
// CompareCoaches — pick 2-3 saved coaches, see them side-by-side.
// Reached from /saved. Reads the pool from /api/favorites (Google-auth gated).
// -----------------------------------------------------------------------------
const MAX_COMPARE = 3;

export default function CompareCoaches() {
  const { user, openSignIn, loading: authLoading } = useAuth();
  const email = user?.email || "";
  const [params, setParams] = useSearchParams();
  const [saved, setSaved] = useState([]);
  const [selected, setSelected] = useState(() => (params.get("slugs") || "").split(",").filter(Boolean).slice(0, MAX_COMPARE));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!email) return setLoading(false);
    axios.get(`${API}/favorites`, { params: { email } })
      .then((r) => setSaved(r.data.coaches || []))
      .catch(() => setSaved([]))
      .finally(() => setLoading(false));
  }, [email]);

  useEffect(() => {
    const sp = new URLSearchParams();
    if (selected.length) sp.set("slugs", selected.join(","));
    setParams(sp, { replace: true });
  }, [selected]); // eslint-disable-line

  const toggle = (slug) => {
    setSelected((cur) =>
      cur.includes(slug) ? cur.filter((s) => s !== slug) : cur.length < MAX_COMPARE ? [...cur, slug] : cur
    );
  };

  const compareCoaches = saved.filter((c) => selected.includes(c.slug));

  if (!user && !authLoading) {
    return (
      <div className="bg-white min-h-screen">
        <MarketplaceNav />
        <section className="max-w-[900px] mx-auto px-6 md:px-10 py-24 text-center">
          <h1 className="font-display text-4xl tracking-tight text-slate-900">Sign in to compare.</h1>
          <p className="mt-3 text-slate-500">Your shortlist is private — Google sign-in required.</p>
          <button
            type="button"
            data-testid="compare-signin-google"
            onClick={() => openSignIn("Sign in to compare your saved coaches.")}
            className="mt-6 inline-flex h-11 px-5 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13px] font-semibold transition-colors"
          >
            Continue with Google →
          </button>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="compare-page" className="bg-white min-h-screen">
      <MarketplaceNav />
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-14 pb-8">
        <div className="eyebrow">§ Compare · pick up to {MAX_COMPARE}</div>
        <h1 className="mt-3 font-display text-[44px] md:text-[64px] leading-[0.98] tracking-[-0.04em] text-slate-900">
          Side-by-side.
        </h1>
        <p className="mt-3 text-slate-600 max-w-[54ch] text-lg">
          Tap a saved coach to add them. Compare price, session type, rating and specialties without hunting through profiles.
        </p>
      </section>

      {/* Picker row */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-8">
        {loading ? (
          <div className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
        ) : saved.length === 0 ? (
          <div data-testid="compare-empty" className="rounded-2xl border border-slate-200 p-6 text-center">
            <p className="text-slate-500 text-[14px]">You haven't saved any coaches yet.</p>
            <Link to="/marketplace" className="mt-3 inline-block text-[#2563EB] font-semibold text-[13px] hover:underline">
              Browse coaches →
            </Link>
          </div>
        ) : (
          <div data-testid="compare-picker" className="flex flex-wrap gap-2">
            {saved.map((c) => {
              const on = selected.includes(c.slug);
              const disabled = !on && selected.length >= MAX_COMPARE;
              return (
                <button
                  key={c.slug}
                  onClick={() => toggle(c.slug)}
                  disabled={disabled}
                  data-testid={`compare-toggle-${c.slug}`}
                  className={`inline-flex items-center gap-2 h-10 px-3 rounded-full border text-[13px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    on ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <img src={c.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                  {c.name}
                  {on ? <Check className="w-3.5 h-3.5" /> : null}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Table */}
      {compareCoaches.length >= 2 && (
        <section data-testid="compare-grid" className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {compareCoaches.map((c) => (
              <article key={c.slug} data-testid={`compare-card-${c.slug}`} className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
                <div className="aspect-[16/10] bg-slate-100 relative">
                  <img src={c.avatar_url} alt={c.name} className="w-full h-full object-cover" />
                  <button
                    onClick={() => toggle(c.slug)}
                    className="absolute top-3 right-3 w-8 h-8 rounded-full grid place-items-center bg-white/95 text-slate-500 hover:text-slate-900"
                    aria-label="Remove from compare"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-5 space-y-3">
                  <div>
                    <div className="font-display text-lg tracking-tight text-slate-900">{c.name}</div>
                    <div className="mt-0.5 text-[12.5px] text-slate-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{c.city}</div>
                  </div>
                  <CoachBadges badges={c.badges} variant="card" />
                  <Row label="Rating" value={
                    <span className="inline-flex items-center gap-1.5">
                      <StarsDisplay value={c.avg_rating} size={11} />
                      {c.review_count > 0 ? `${c.avg_rating.toFixed(1)} (${c.review_count})` : "New"}
                    </span>
                  } />
                  <Row label="Price / session" value={<>${c.price_per_session} <span className="text-slate-400">/ {c.session_duration_minutes}m</span></>} />
                  <Row label="Session type" value={
                    <span className="inline-flex flex-wrap gap-1.5">
                      {c.online_available && <Chip icon={Video} tone="blue" label="Online" />}
                      {c.in_person_available && <Chip icon={MapPin} tone="green" label="In person" />}
                    </span>
                  } />
                  <Row label="Timezone" value={c.timezone} />
                  <Row label="Experience" value={`${c.years_experience} yrs`} />
                  <Row label="Languages" value={
                    <span className="inline-flex items-center gap-1"><Globe2 className="w-3 h-3" />{(c.languages || []).join(" · ")}</span>
                  } />
                  <Row label="Specialties" value={
                    <div className="flex flex-wrap gap-1">
                      {(c.specialties || []).slice(0, 4).map((s) => (
                        <span key={s} className="text-[10.5px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  } />
                  <div className="pt-3 border-t border-slate-100">
                    <Link
                      to={`/coach/${c.slug}`}
                      data-testid={`compare-book-${c.slug}`}
                      className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13px] font-semibold transition-colors"
                    >
                      View & book <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {compareCoaches.length < 2 && saved.length >= 2 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 text-center text-slate-500 text-[14px]">
          Pick at least two coaches above to see the side-by-side view.
        </section>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-3 text-[13px] py-1.5 border-b border-slate-50 last:border-b-0">
      <div className="text-[10.5px] tracking-[0.18em] uppercase text-slate-400 font-semibold w-[38%] shrink-0">{label}</div>
      <div className="text-slate-800 min-w-0">{value}</div>
    </div>
  );
}

function Chip({ icon: Icon, tone, label }) {
  const bg = tone === "blue" ? "bg-[#2563EB]/8 text-[#2563EB]" : "bg-emerald-50 text-emerald-700";
  return (
    <span className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10.5px] font-semibold ${bg}`}>
      <Icon className="w-3 h-3" strokeWidth={2.4} /> {label}
    </span>
  );
}
